// src/screens/EditProfile.tsx
import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { PrimaryButton } from "../components/Button";
import LabeledInput from "../components/LabeledInput";
import ExperienceGrid from "../components/ExperienceGrid";
import PageTitle from "../components/PageTitle";
import { getUserProfile, updateProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

export default function EditProfile() {
    const { user } = useAuth();

    const [name, setName] = useState("");
    const [experience, setExperience] = useState<"Beginner" | "Intermediate" | "Professional">("Beginner");
    const [age, setAge] = useState<string>("");
    const [gender, setGender] = useState<"Male" | "Female">("Male");
    const [hasGloves, setHasGloves] = useState<boolean | null>(null);
    const [gloveSize, setGloveSize] = useState<number | null>(8);
    const [hasHandwraps, setHasHandwraps] = useState<boolean | null>(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Load user profile on component mount
    useEffect(() => {
        const loadUserProfile = async () => {
            if (!user?.id) {
                setLoading(false);
                return;
            }

            try {
                const response = await getUserProfile(user.id);
                const userData = response.user;

                setName(userData.name || "");
                setAge(userData.age ? String(userData.age) : "");
                setExperience((userData.experience as "Beginner" | "Intermediate" | "Professional") || "Beginner");
                setGender((userData.sex as "Male" | "Female") || "Male");
                setHasGloves(userData.hasGloves ?? null);
                setGloveSize(userData.gloveSize ?? 8);
                setHasHandwraps(userData.hasHandwrap ?? null);
            } catch (error) {
                console.error("Error loading user profile:", error);
                setError("Failed to load profile");
            } finally {
                setLoading(false);
            }
        };

        loadUserProfile();
    }, [user?.id]);

    const save = async () => {
        if (!user?.id) {
            setError("User not authenticated");
            return;
        }

        if (!name || !age || !gender || hasGloves === null || gloveSize === null || hasHandwraps === null) {
            setError("Please fill in all required fields");
            return;
        }

        setSaving(true);
        setError("");

        try {
            await updateProfile({
                _id: user.id,
                name,
                age: parseInt(age),
                sex: gender,
                experience,
                hasGloves,
                gloveSize,
                hasHandwrap: hasHandwraps
            });

            // Navigate back or show success message
            window.history.back();
        } catch (error) {
            console.error("Error updating profile:", error);
            setError("Failed to update profile. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Show loading state while profile is loading
    if (loading) {
        return (
            <div className="h-[100dvh] w-full bg-[#F7F7F7] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading. Please wait.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="w-full min-h-full flex flex-col">
                {/* NAVBAR */}
                <Navbar
                    onBack={() => window.history.back()}
                    background="transparent"
                    spacerHeight={40}
                />

                {/* CONTENT */}
                <main className="flex-1 px-5">
                    <div className="pt-4">
                        {/* TITLE */}
                        <PageTitle>Edit Profile</PageTitle>

                        {/* name */}
                        <div className="mb-7">
                            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                What should trainers call you?
                            </label>
                            <div className="border border-[#B1B1B1] bg-white px-4 h-[49px] flex items-center">
                                <input
                                    type="text"
                                    placeholder="Your Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full font-manrope text-[14px] text-[#2d2d2d] placeholder:text-[#717171] outline-none bg-transparent"
                                />
                            </div>
                        </div>

                        {/* experience */}
                        <ExperienceGrid
                            label="Your Muaythai Experience"
                            value={experience}
                            onChange={setExperience}
                            className="mb-7"
                        />

                        {/* age + gender */}
                        <div className="grid grid-cols-2 gap-7 mb-7">
                            <div>
                                <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                    How old are you?
                                </label>
                                <div className="border border-[#B1B1B1] bg-white px-4 h-[49px] flex items-center">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        placeholder="Your Age"
                                        value={age}
                                        onChange={(e) => setAge(e.target.value)}
                                        className="w-full font-manrope text-[14px] placeholder:text-[#717171] outline-none bg-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                    Division (Sex)
                                </label>
                                <div className="flex gap-3">
                                    {(["Male", "Female"] as const).map((g) => {
                                        const selected = gender === g;
                                        return (
                                            <button
                                                key={g}
                                                type="button"
                                                onClick={() => setGender(g)}
                                                className={`w-[72px] h-[48px] border bg-white grid place-items-center ${selected ? "border-[#D62422]" : "border-[#B1B1B1]"
                                                    }`}
                                            >
                                                <span className={`text-[12px] ${selected ? "text-black" : "text-[#717171]"}`}>
                                                    {g === "Male" ? "M" : "F"}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* DO YOU HAVE GLOVES */}
                        <section className="mb-6">
                            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                Do you have a gloves
                            </label>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    aria-pressed={hasGloves === true}
                                    onClick={() => setHasGloves(true)}
                                    className={`flex-1 h-12 grid place-items-center border bg-white ${hasGloves === true ? "border-[#D62422]" : "border-[#B1B1B1]"
                                        }`}
                                >
                                    <span className={hasGloves === true ? "text-black" : "text-[#717171] text-[12px]"}>Yes</span>
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={hasGloves === false}
                                    onClick={() => setHasGloves(false)}
                                    className={`flex-1 h-12 grid place-items-center border bg-white ${hasGloves === false ? "border-[#D62422]" : "border-[#B1B1B1]"
                                        }`}
                                >
                                    <span className={hasGloves === false ? "text-black" : "text-[#717171] text-[12px]"}>No</span>
                                </button>
                            </div>
                        </section>

                        {/* YOUR GLOVE SIZE */}
                        <section className="mb-7">
                            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                Your glove size (in oz)
                            </label>
                            <div className="flex gap-3">
                                {[8, 10, 12, 14].map((s) => {
                                    const sizeMap: Record<number, string> = { 8: 'XS', 10: 'S', 12: 'M', 14: 'L' };
                                    return (
                                        <button
                                            key={s}
                                            type="button"
                                            aria-pressed={gloveSize === s}
                                            onClick={() => setGloveSize(s)}
                                            className={`w-10 h-12 grid place-items-center border bg-white ${gloveSize === s ? "border-[#D62422]" : "border-[#B1B1B1]"
                                                }`}
                                        >
                                            <span className={`text-[12px] ${gloveSize === s ? "text-black" : "text-[#717171]"}`}>
                                                {sizeMap[s]}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>

                        {/* DO YOU HAVE HANDWRAPS */}
                        <section className="mb-2">
                            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                                Do you have handwraps
                            </label>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    aria-pressed={hasHandwraps === true}
                                    onClick={() => setHasHandwraps(true)}
                                    className={`flex-1 h-12 grid place-items-center border bg-white ${hasHandwraps === true ? "border-[#D62422]" : "border-[#B1B1B1]"
                                        }`}
                                >
                                    <span className={hasHandwraps === true ? "text-black" : "text-[#717171] text-[12px]"}>Yes</span>
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={hasHandwraps === false}
                                    onClick={() => setHasHandwraps(false)}
                                    className={`flex-1 h-12 grid place-items-center border bg-white ${hasHandwraps === false ? "border-[#D62422]" : "border-[#B1B1B1]"
                                        }`}
                                >
                                    <span className={hasHandwraps === false ? "text-black" : "text-[#717171] text-[12px]"}>No</span>
                                </button>
                            </div>
                        </section>

                        {/* Error Display */}
                        {error && (
                            <div className="pt-4">
                                <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded">
                                    {error}
                                </div>
                            </div>
                        )}

                        {/* SAVE BUTTON AT BOTTOM OF SCROLLVIEW */}
                        <div className="pt-8 pb-[max(16px,env(safe-area-inset-bottom))]">
                            <PrimaryButton
                                label={saving ? "SAVING..." : "SAVE CHANGES"}
                                onClick={save}
                                disabled={saving}
                                className="!w-full"
                            />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
